import { Controller } from "@hotwired/stimulus"
import { PollingService } from "../../services/polling_service"
import { OnboardingApi } from "../../services/onboarding_api"
import { renderPersonaCard } from "../../renderers/persona_renderer"
import { escapeHtml } from "../../utils/html_helpers"

// Every status that means the run is OVER. Membership here is the single rule
// that decides whether the spinner comes down, so a new outcome cannot be added
// server-side and quietly keep spinning: `partial` is in this list for exactly
// that reason.
// `refused` is terminal too (KI-474): the cap stopped the run before it began.
const TERMINAL_STATUSES = ["completed", "complete", "partial", "failed", "refused"]

// Measured, not guessed. The old copy promised half a minute at the outside; the
// three production runs on 10 August 2026 took 69, 76 and 80 seconds, so it was
// wrong by a factor of three in the direction that makes a stuck run look
// perfectly normal to the person watching it. Persona
// generation now runs as a plan call plus one concurrent call per persona, which
// should land nearer a single call than four — re-measure and edit this line
// rather than letting it drift back into fiction.
const GENERATION_ESTIMATE_COPY = "Usually 40-90 seconds"

// Stop polling rather than hammer a server that is not answering. Five failures
// at 2s is ~10 seconds of silence, which is far longer than a transient blip and
// far shorter than the run itself.
const MAX_CONSECUTIVE_POLL_FAILURES = 5

// What the person agrees to before a regenerate (KI-469). Archived, not
// deleted, and it says so: the ICP page keeps the rows and can restore them.
const REGENERATE_CONFIRM =
  "Regenerate ICPs? The current ICPs for this company, including any you have edited or approved, " +
  "will be archived and a new set generated. Archived ICPs are kept on the ICP page, not deleted."

// A hard ceiling on how long the screen may claim to be working. Nothing in this
// feature legitimately takes five minutes; past that the poller has lost the run
// and must say so instead of spinning indefinitely.
const POLL_TIMEOUT_MS = 5 * 60 * 1000

// ICP controller - handles persona generation, rendering, and editing
export default class extends Controller {
  static values = { sessionId: String, icpStatus: String }

  static targets = [
    "personaOutput", "icpStatusBadge", "icpStatusMessage",
    "icpLoadingIndicator", "icpButtonRow", "generateIcpButton", "regenerateButton",
    "capRefusal"
  ]

  static outlets = ["onboarding-wizard"]

  connect() {
    this.api = new OnboardingApi(this.sessionIdValue)
    this.pollingService = null
    this.elapsedTimerId = null
    this.generationStartedAt = null
    this.pollFailureCount = 0

    // Bind event handlers
    this.handleApprovePersona = this.handleApprovePersona.bind(this)
    this.handleUnapprovePersona = this.handleUnapprovePersona.bind(this)
    this.handleEditPersona = this.handleEditPersona.bind(this)

    window.addEventListener('approve-persona', this.handleApprovePersona)
    window.addEventListener('unapprove-persona', this.handleUnapprovePersona)
    window.addEventListener('edit-persona', this.handleEditPersona)

    // A session that has run gets its personas drawn on connect (KI-470). The
    // wizard used to ask for this through an outlet that was never wired, so a
    // page opened on the personas panel showed "Loading ICPs..." for ever. A
    // run still in flight is picked up by the same call, which starts polling.
    // An idle session has nothing to fetch and must not start a poller.
    if (this.icpStatusValue && this.icpStatusValue !== "idle") {
      this.refreshStatus()
    }
  }

  disconnect() {
    this.finishPolling()
    window.removeEventListener('approve-persona', this.handleApprovePersona)
    window.removeEventListener('unapprove-persona', this.handleUnapprovePersona)
    window.removeEventListener('edit-persona', this.handleEditPersona)
  }

  async generate(event) {
    if (event) event.preventDefault()
    await this.startRun({ regenerate: false })
  }

  // The explicit replacement of a finished set (KI-469). Confirmed first,
  // because the server will archive every ICP this session generated, edited
  // ones included, before it generates again. Archived, not deleted: the rows
  // stay on the ICP page.
  async regenerate(event) {
    if (event) event.preventDefault()
    if (!window.confirm(REGENERATE_CONFIRM)) return
    await this.startRun({ regenerate: true })
  }

  async startRun({ regenerate }) {
    console.log(`[icpController] Starting ICP ${regenerate ? 're' : ''}generation`)

    if (this.hasRegenerateButtonTarget) this.regenerateButtonTarget.disabled = true

    // Show loading indicator on the Review step (Step 4)
    if (this.hasIcpLoadingIndicatorTarget && this.hasIcpButtonRowTarget) {
      this.icpLoadingIndicatorTarget.classList.remove('hidden')
      this.icpLoadingIndicatorTarget.classList.add('flex')
      this.icpButtonRowTarget.classList.add('hidden')
    }

    // Also disable the generate button if visible
    if (this.hasGenerateIcpButtonTarget) {
      this.generateIcpButtonTarget.disabled = true
      this.generateIcpButtonTarget.textContent = "Generating..."
    }

    // Show processing status immediately with spinner
    if (this.hasIcpStatusBadgeTarget) {
      this.icpStatusBadgeTarget.textContent = "Processing..."
    }
    if (this.hasIcpStatusMessageTarget) {
      this.icpStatusMessageTarget.textContent = "Generating Ideal Customer Profiles with AI..."
    }

    // Show loading spinner in persona output area (for Step 5)
    if (this.hasPersonaOutputTarget) {
      this.personaOutputTarget.innerHTML = this.spinnerHtml()
    }

    try {
      // Get payload from wizard controller
      const payload = this.getIcpPayload()
      const data = regenerate
        ? await this.api.regenerateIcp(payload)
        : await this.api.generateIcp(payload)

      if (data.status === "queued" || data.status === "processing") {
        // Start polling for completion
        this.startPolling()
      } else {
        // Anything else is not a run in flight, and leaving the spinner up would
        // claim one. Ask the server what state it is actually in. A finished
        // session answers a plain generate this way (KI-469): the personas it
        // already has are drawn, the wizard moves to them, and the notice says
        // that Regenerate is the way to replace them.
        await this.refreshStatus()
        if (data.notice) this.paintMessage(data.notice)
      }
    } catch (error) {
      console.error('[icpController] Generation error:', error)
      this.finishPolling()

      // The server's own words where it sent any. The commonest rejection here is
      // "Please describe your market and the problems you solve before generating",
      // which is a fixable mistake, and answering it with a generic "please try
      // again" tells the person to repeat exactly what did not work.
      const reason = this.serverReason(error)
      this.paintMessage(reason || 'Error generating ICP.')
      this.renderTerminalFailure(reason || 'Error generating ICP. Please try again.')
    }
  }

  // OnboardingApi throws `HTTP <status>: <body>` on a non-2xx. Dig the server's
  // message out of the body if there is one; return null rather than guess.
  serverReason(error) {
    const body = String(error && error.message).match(/\{.*\}/s)
    if (!body) return null

    try {
      const parsed = JSON.parse(body[0])
      return parsed.error || parsed.message || null
    } catch (_ignored) {
      return null
    }
  }

  resetLoadingState() {
    // Reset the loading indicator on Step 4
    if (this.hasIcpLoadingIndicatorTarget && this.hasIcpButtonRowTarget) {
      this.icpLoadingIndicatorTarget.classList.add('hidden')
      this.icpLoadingIndicatorTarget.classList.remove('flex')
      this.icpButtonRowTarget.classList.remove('hidden')
    }

    if (this.hasGenerateIcpButtonTarget) {
      this.generateIcpButtonTarget.disabled = false
      this.generateIcpButtonTarget.textContent = "Generate ICP"
    }

    if (this.hasRegenerateButtonTarget) this.regenerateButtonTarget.disabled = false
  }

  startPolling(startedAtMs = null) {
    if (this.pollingService) {
      this.pollingService.stop()
    }

    this.pollFailureCount = 0
    this.startElapsedTicker(startedAtMs)

    this.pollingService = new PollingService('', 2000)
    this.pollingService.start(() => this.refreshStatus())
  }

  stopPolling() {
    if (this.pollingService) {
      this.pollingService.stop()
      this.pollingService = null
    }
  }

  // THE ONLY EXIT FROM POLLING, and the reason there is exactly one: whatever
  // ends a run — success, failure, a dead server, a broken render, the timeout —
  // has to take the spinner down with it. When the teardown was spread across
  // the call sites, the `failed` branch simply forgot, and a failed generation
  // left "Generating Ideal Customer Profiles..." on screen for ever (KI-227, observed on
  // production minutes after the status column started telling the truth).
  finishPolling() {
    this.stopPolling()
    this.stopElapsedTicker()
    this.resetLoadingState()
  }

  async refreshStatus(event) {
    if (event) event.preventDefault()

    if (this.pollingTimedOut()) {
      this.finishPolling()
      this.renderTerminalFailure(
        "Generation has not reported back in five minutes. Reload the page to see where it got to."
      )
      return
    }

    let data
    try {
      data = await this.api.getIcpStatus()
    } catch (error) {
      console.error('[icpController] Status check error:', error)
      this.recordPollFailure(error)
      return
    }

    this.pollFailureCount = 0

    // Rendering is wrapped SEPARATELY from the fetch, because the two fail for
    // different reasons and only one of them used to be caught at all. A throw in
    // here is invisible: setInterval swallows it, the callback never runs again,
    // polling stops with nothing on screen to say so, and the spinner becomes
    // permanent. So a render failure is treated as terminal and SAYS so — the one
    // thing it must never do is look like work in progress.
    try {
      this.applyStatus(data)
    } catch (error) {
      console.error('[icpController] Render error:', error)
      this.finishPolling()
      this.renderTerminalFailure(
        "This page lost track of the generation. Reload to see where it got to."
      )
    }
  }

  applyStatus(data) {
    const status = data && data.status

    this.paintBadge(status, data && data.status_label)
    this.paintMessage(data && data.message)

    // Nothing has run. Not terminal and not in flight either, so neither the
    // spinner nor the poller has any business starting; say so instead.
    if (status === "idle") {
      this.finishPolling()
      this.renderPersonas([])
      return
    }

    if (!TERMINAL_STATUSES.includes(status)) {
      // Still running. If nothing is polling — a fresh page load mid-run, or a
      // click on Refresh — pick the run up rather than show a frozen snapshot.
      if (!this.pollingService) this.startPolling(this.parseRequestedAt(data))
      return
    }

    this.finishPolling()

    if (status === "failed") {
      this.renderTerminalFailure(data.message || "ICP generation failed. Please try again.")
      return
    }

    // The cap refused the run before it started (KI-474). Not a retry: the
    // reason and the remedy are shown where the person stands and Generate
    // stays disabled, after finishPolling has just re-enabled it.
    if (status === "refused") {
      this.renderCapRefusal(data.message)
      return
    }

    // Unconditional, for both completed and partial. This call used to sit behind
    // a non-empty length check, so a completion that produced nothing left the
    // spinner running — the same stranding bug as the failed branch, one status
    // along. renderPersonas states the empty case out loud instead.
    this.renderPersonas(data.personas, { shortfall: status === "partial" ? data.message : null })

    this.dispatch("generation-complete", { detail: { personas: data.personas, status: status } })
  }

  paintBadge(status, label) {
    if (!this.hasIcpStatusBadgeTarget) return

    this.icpStatusBadgeTarget.textContent = label || this.badgeLabel(status)
    this.applyStatusBadgeClasses(status)
  }

  badgeLabel(status) {
    if (!status) return "Unknown"
    return status.charAt(0).toUpperCase() + status.slice(1)
  }

  paintMessage(message) {
    if (!this.hasIcpStatusMessageTarget) return

    this.icpStatusMessageTarget.textContent = message || ""
  }

  // Repaint the status badge to match the status just polled.
  //
  // Server-side the badge is coloured by IcpResultsComponent#icp_status_badge_classes.
  // The poller changes the status WITHOUT a re-render, so without this the colours
  // stay on whatever the page was rendered with — which is how a failed generation
  // showed the word "Failed" in success green (KI-227). Keep this map in step with
  // the Ruby one; both halves are needed for the badge to be honest.
  //
  // It is guarded against a missing target, and every token below is a non-empty
  // string with no whitespace, which are the only two ways classList throws. It is
  // ALSO called from inside refreshStatus's render try/catch, which is the real
  // guarantee: correctness here is checked, but nothing in this file is trusted to
  // be exception-free, because an uncaught throw anywhere in a poll callback
  // strands the UI.
  applyStatusBadgeClasses(status) {
    if (!this.hasIcpStatusBadgeTarget) return

    const palettes = {
      completed:  ["bg-green-100", "dark:bg-green-900/30", "text-green-700", "dark:text-green-400", "border-green-300", "dark:border-green-700"],
      partial:    ["bg-orange-100", "dark:bg-orange-900/30", "text-orange-700", "dark:text-orange-400", "border-orange-300", "dark:border-orange-700"],
      failed:     ["bg-red-100", "dark:bg-red-900/30", "text-red-700", "dark:text-red-400", "border-red-300", "dark:border-red-700"],
      refused:    ["bg-orange-100", "dark:bg-orange-900/30", "text-orange-700", "dark:text-orange-400", "border-orange-300", "dark:border-orange-700"],
      processing: ["bg-amber-100", "dark:bg-amber-900/30", "text-amber-700", "dark:text-amber-400", "border-amber-300", "dark:border-amber-700"],
      idle:       ["bg-theme-secondary/20", "text-theme-muted", "border-theme-secondary"]
    }

    const badge = this.icpStatusBadgeTarget
    Object.values(palettes).flat().forEach((cls) => badge.classList.remove(cls))
    ;(palettes[status] || palettes.idle).forEach((cls) => badge.classList.add(cls))
  }

  recordPollFailure(error) {
    this.pollFailureCount = (this.pollFailureCount || 0) + 1
    if (this.pollFailureCount < MAX_CONSECUTIVE_POLL_FAILURES) return

    this.finishPolling()
    this.renderTerminalFailure(
      "Lost contact with the server while generating. Reload the page to see the result."
    )
  }

  pollingTimedOut() {
    if (!this.generationStartedAt) return false

    return (Date.now() - this.generationStartedAt) > POLL_TIMEOUT_MS
  }

  // Elapsed time exists so a person can tell working from stuck. A spinner alone
  // looks identical at 5 seconds and at 5 minutes, which is precisely why a run
  // that died at 80 seconds could sit on screen unnoticed.
  startElapsedTicker(startedAtMs = null) {
    this.stopElapsedTicker()
    this.generationStartedAt = startedAtMs || Date.now()
    this.paintElapsed()
    this.elapsedTimerId = setInterval(() => this.paintElapsed(), 1000)
  }

  stopElapsedTicker() {
    if (this.elapsedTimerId) {
      clearInterval(this.elapsedTimerId)
      this.elapsedTimerId = null
    }
  }

  paintElapsed() {
    if (!this.generationStartedAt) return

    const element = this.element.querySelector('[data-icp-elapsed]')
    if (!element) return

    const seconds = Math.max(0, Math.round((Date.now() - this.generationStartedAt) / 1000))
    element.textContent = `${seconds}s elapsed`
  }

  // The server's icp_requested_at, so a page opened mid-run counts from when the
  // run actually started rather than from when this tab happened to load.
  parseRequestedAt(data) {
    const requestedAt = data && data.requested_at
    if (!requestedAt) return null

    const parsed = Date.parse(requestedAt)
    return Number.isNaN(parsed) ? null : parsed
  }

  spinnerHtml() {
    return `
      <div class="col-span-2 flex flex-col items-center justify-center py-12 text-center">
        <svg class="w-12 h-12 text-k3c-coral mb-4" style="animation: spin 1s linear infinite;" viewBox="0 0 24 24" fill="none">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-theme-primary font-medium">Generating Ideal Customer Profiles...</p>
        <p class="text-sm text-theme-muted mt-1">${GENERATION_ESTIMATE_COPY}</p>
        <p class="text-sm text-theme-muted mt-1 tabular-nums" data-icp-elapsed>0s elapsed</p>
      </div>
    `
  }

  // The cap refused the run (KI-474). The block carrying the reason and the
  // remedy is server-rendered and hidden, so there is one copy of the words;
  // this reveals it, writes the server's reason into it, and takes Generate and
  // Regenerate away. Lifted only by a page load once the client has room
  // (Onboarding::ReconsidersIcpRefusal), never from here.
  renderCapRefusal(message) {
    const reason = message || "You already have the maximum of 3 ICPs."

    // The block's title, body, link and (for super admins) extra line are
    // server-rendered; revealing it is all a live refusal needs.
    this.capRefusalTargets.forEach((block) => block.classList.remove("hidden"))

    if (this.hasGenerateIcpButtonTarget) {
      this.generateIcpButtonTarget.disabled = true
      this.generateIcpButtonTarget.textContent = "Generate ICP"
    }
    if (this.hasRegenerateButtonTarget) this.regenerateButtonTarget.disabled = true

    if (this.hasPersonaOutputTarget) {
      this.personaOutputTarget.innerHTML = `
        <div class="col-span-2 border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 text-orange-800 dark:text-orange-300 text-sm">
          ${escapeHtml(reason)}
        </div>
      `
    }
  }

  // A terminal state on screen, always with a reason. Anything that ends polling
  // renders one of these; nothing ends polling silently.
  renderTerminalFailure(message) {
    if (!this.hasPersonaOutputTarget) return

    this.personaOutputTarget.innerHTML = `
      <div class="col-span-2 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-xl p-4 text-red-700 dark:text-red-400 text-sm">
        ${escapeHtml(message || "ICP generation failed. Please try again.")}
      </div>
    `
  }

  // Load personas (called by wizard when navigating to persona step)
  async loadPersonas() {
    console.log('[icpController] Loading personas')
    await this.refreshStatus()
  }

  renderPersonas(personas, { shortfall = null } = {}) {
    if (!this.hasPersonaOutputTarget) return

    if (!Array.isArray(personas) || !personas.length) {
      this.personaOutputTarget.innerHTML = `
        <div class="col-span-2 border border-theme-secondary rounded-xl p-4 text-theme-muted text-sm">
          No ICPs generated yet. Try "Generate ICP" first.
        </div>
      `
      return
    }

    // The shortfall banner is rendered WITH the personas, not instead of them.
    // A partial run produced real, paid-for personas and they are worth showing;
    // what the person must not have to do is count the cards to discover one is
    // missing.
    const banner = shortfall
      ? `<div class="col-span-2 border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 text-orange-800 dark:text-orange-300 text-sm">
           ${escapeHtml(shortfall)}
         </div>`
      : ""

    this.personaOutputTarget.innerHTML = banner + personas
      .map(p => renderPersonaCard(p, escapeHtml))
      .join("")
  }

  // Event handlers for persona approval/editing
  handleApprovePersona(event) {
    const personaId = event.detail?.id
    if (!personaId) return
    this.updatePersonaStatus(personaId, 'approved')
  }

  handleUnapprovePersona(event) {
    const personaId = event.detail?.id
    if (!personaId) return
    this.updatePersonaStatus(personaId, 'draft')
  }

  async updatePersonaStatus(personaId, status) {
    try {
      await this.api.updatePersonaStatus(personaId, status)
      // Refresh the persona display
      await this.refreshStatus()
    } catch (error) {
      console.error('[icpController] Update status error:', error)
    }
  }

  handleEditPersona(event) {
    const personaId = event.detail?.id
    if (!personaId) return

    // Find the persona card
    const card = this.element.querySelector(`[data-persona-id="${personaId}"]`)
    if (!card) return

    // Get current data from the card
    const name = card.querySelector('h3')?.textContent || ''
    const headline = card.querySelector('h3 + p')?.textContent || ''

    // Show edit modal
    this.showPersonaEditModal(personaId, { name, headline })
  }

  showPersonaEditModal(personaId, currentData) {
    // Create a simple modal overlay
    const modal = document.createElement('div')
    modal.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50'
    modal.id = 'persona-edit-modal'

    modal.innerHTML = `
      <div class="bg-white rounded-xl p-6 max-w-lg w-full mx-4 space-y-4">
        <h3 class="text-lg font-semibold text-theme-primary">Edit ICP</h3>

        <div class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-theme-primary mb-1">Name</label>
            <input type="text" id="edit-persona-name" value="${escapeHtml(currentData.name)}"
              class="w-full px-3 py-2 border border-theme-secondary rounded-lg text-sm">
          </div>
          <div>
            <label class="block text-sm font-medium text-theme-primary mb-1">Headline</label>
            <input type="text" id="edit-persona-headline" value="${escapeHtml(currentData.headline)}"
              class="w-full px-3 py-2 border border-theme-secondary rounded-lg text-sm">
          </div>
        </div>

        <div class="flex items-center justify-end gap-3 pt-2">
          <button type="button" id="cancel-edit-persona"
            class="px-4 py-2 text-sm font-medium rounded-lg border border-theme-secondary text-theme-primary hover:bg-theme-secondary/20">
            Cancel
          </button>
          <button type="button" id="save-edit-persona"
            class="px-4 py-2 text-sm font-medium rounded-lg bg-theme-primary text-white hover:opacity-90">
            Save Changes
          </button>
        </div>
      </div>
    `

    document.body.appendChild(modal)

    // Handle cancel
    modal.querySelector('#cancel-edit-persona').addEventListener('click', () => modal.remove())
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove() })

    // Handle save
    modal.querySelector('#save-edit-persona').addEventListener('click', () => {
      const newName = modal.querySelector('#edit-persona-name').value
      const newHeadline = modal.querySelector('#edit-persona-headline').value
      this.savePersonaEdit(personaId, { name: newName, headline: newHeadline })
      modal.remove()
    })
  }

  async savePersonaEdit(personaId, data) {
    try {
      await this.api.updatePersona(personaId, data)
      // Refresh the persona display
      await this.refreshStatus()
    } catch (error) {
      console.error('[icpController] Save edit error:', error)
    }
  }

  // Get ICP payload from wizard controller
  getIcpPayload() {
    // Try outlet first
    if (this.hasOnboardingWizardOutlet) {
      return this.onboardingWizardOutlet.buildIcpPayload()
    }

    // Fallback: Find wizard controller on the same element
    const wizardController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'onboarding-wizard'
    )

    if (wizardController) {
      return wizardController.buildIcpPayload()
    }

    return {}
  }
}
